import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, useToast, Collapse, Button, useDisclosure } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import ZaraButton from 'components/custom-components/buttons/Button';
import { useChangeTrainingGuideMutation } from 'graphql/generated';
import { useError } from 'error-provider/ErrorProvider';
import { useApolloClient } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import DiffViewer from 'pages/agent-configuration/DiffViewer';

interface FullAgentConfigTrainingGuideProps {
    trainingGuideData?: string | null;
}

const schema = z.object({
    newGuide: z.string().min(1, 'Required'),
});

type Schema = z.infer<typeof schema>;

const FullAgentConfigTrainingGuide = ({ trainingGuideData }: FullAgentConfigTrainingGuideProps) => {
    const { isOpen, onToggle } = useDisclosure();
    const { control, handleSubmit, setValue, watch } = useForm<Schema>({
        defaultValues: {
            newGuide: trainingGuideData || '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });

    const currentGuideValue = watch('newGuide');

    const apolloClient = useApolloClient();
    const { showError } = useError();
    const toast = useToast();
    const params = useParams();

    const [changeGuide, { loading: isChangeGuideLoading }] = useChangeTrainingGuideMutation({
        onError(error) {
            showError(undefined, error);
        },
    });

    const onSubmit = (form: Schema) => {
        changeGuide({
            variables: {
                newGuide: form.newGuide,
                agentId: params.id as string,
            },
            onCompleted(data) {
                if (data.changeTrainingGuide.__typename === 'SuccessType') {
                    toast({
                        title: data.changeTrainingGuide.message,
                        status: 'success',
                        isClosable: true,
                    });
                    apolloClient.cache.modify({
                        id: `AgentType:${params.id as string}`,
                        fields: {
                            trainingGuide() {
                                return form.newGuide;
                            },
                        },
                    });
                }
                if (data.changeTrainingGuide.__typename === 'ErrorType') {
                    toast({
                        title: data.changeTrainingGuide.message,
                        status: 'error',
                        isClosable: true,
                    });
                }
            },
        });
    };

    useEffect(() => {
        if (trainingGuideData) {
            setValue('newGuide', trainingGuideData);
        }
    }, [trainingGuideData, setValue]);

    return (
        <Stack spacing='4'>
            <div className='pt-6 border-t'>
                <div className='flex items-start justify-between'>
                    <div>
                        <h3 className='text-lg font-semibold mb-2'>Training Guide</h3>
                        <p className='text-sm text-[#71717a] mb-4'>
                            Core instructions for persona and constraints
                        </p>
                    </div>
                    <Button
                        aria-label={isOpen ? 'Collapse training guide' : 'Expand training guide'}
                        size='sm'
                        colorScheme='teal'
                        onClick={onToggle}
                        leftIcon={<ChevronDownIcon transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'} transition='transform 0.2s' />}
                    >
                        {isOpen ? 'Collapse' : 'Expand'}
                    </Button>
                </div>
            </div>

            <Collapse in={isOpen} animateOpacity>
                <Stack as='form' onSubmit={handleSubmit(onSubmit)} alignItems='self-start'>
                    <Controller
                        control={control}
                        name='newGuide'
                        render={({ field: { onChange, onBlur, value } }) => (
                            <textarea
                                required
                                placeholder='Enter training guide here...'
                                className='flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[200px]'
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                            />
                        )}
                    />
                    <p className='text-xs text-[#71717a]'>Define the agent's role, tone, and key guidelines.</p>

                    {/*  diff viewer can be added back when needed, currently removed to simplify the UI and avoid confusion for users who may not understand the purpose of the diff viewer
                    <DiffViewer originalText={trainingGuideData || ''} currentText={currentGuideValue} />
                    */}

                    <ZaraButton type='submit' variant='primary' w='auto' fontSize='sm' isLoading={isChangeGuideLoading}>
                        Update Training Guide
                    </ZaraButton>
                </Stack>
            </Collapse>
        </Stack>
    );
};

export default FullAgentConfigTrainingGuide;
